import { ReactElement, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { loadableReady } from '@loadable/component';

import {
  PATH_DATA_ATTRIBUTE,
  CONTENT_DATA_ATTRIBUTE,
  WINDOW_DATA_PROPERTY_NAME,
} from 'constants/render';
import { CHUNK_LOADING_GLOBAL } from 'constants/loadable';
import { onError } from './apmOnError';

export type RootData = ReturnType<typeof getRootData>;

type HydrationConfig = {
  appElement: ReactElement;
  loggerName: string;
  rootData: RootData;
};

export const getRootData = () => {
  const rootContainer = document.currentScript?.closest?.(`[${PATH_DATA_ATTRIBUTE}]`);
  const namespace = rootContainer?.getAttribute?.(PATH_DATA_ATTRIBUTE);

  if (!namespace) {
    return { rootContainer, contentRoot: null, namespace: null, content: null, config: null };
  }

  const contentRoot = rootContainer?.querySelector?.(`[${CONTENT_DATA_ATTRIBUTE}="${namespace}"]`);

  const { content, config } = window[WINDOW_DATA_PROPERTY_NAME] || {};
  const contentData = content?.[namespace];

  return { rootContainer, contentRoot, namespace, content: contentData, config };
};

export const hydrateClient = ({ appElement, loggerName, rootData }: HydrationConfig) => {
  const { rootContainer, contentRoot, namespace } = rootData;

  if (!rootContainer || !namespace || !contentRoot) {
    const error = new Error('Content Renderer: Root container or content root not found');

    return onError(error, {
      logger: `ContentRenderer | ${loggerName}`,
      section: 'Hydration Error',
    });
  }

  loadableReady(
    () =>
      hydrateRoot(contentRoot, <StrictMode>{appElement}</StrictMode>, {
        identifierPrefix: `wtr-cr-${namespace}`,
      }),
    { namespace, chunkLoadingGlobal: CHUNK_LOADING_GLOBAL },
  );
};
